import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Home, Calendar, FileText, 
  UserPlus, LogOut, Menu, X, Zap, UserCheck, CalendarClock, Shield, Activity, HelpCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/gydjscwr_attachment_1.jpeg";

const adminNavItems = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Pipeline (CRM)', path: '/admin/leads', icon: Users },
  { name: 'Activities', path: '/admin/activities', icon: Activity },
  { name: 'Appointments', path: '/admin/appointments', icon: CalendarClock },
  { name: 'Automations', path: '/admin/automations', icon: Zap },
  { name: 'Agents', path: '/admin/agents', icon: UserCheck },
  { name: 'Team Members', path: '/admin/team', icon: UserPlus },
  { name: 'Properties', path: '/admin/properties', icon: Home },
  { name: 'Open Houses', path: '/admin/open-houses', icon: Calendar },
  { name: 'Applications', path: '/admin/applications', icon: FileText },
  { name: 'Help Guide', path: '/admin/help', icon: HelpCircle },
];

const agentNavItems = [
  { name: 'My Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'My Leads', path: '/admin/leads', icon: Users },
  { name: 'My Appointments', path: '/admin/appointments', icon: CalendarClock },
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    verifyToken(token);
  }, [navigate]);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/verify?token=${token}`);
      setAdmin(response.data);
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      navigate('/admin');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  if (!admin) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const isAgent = admin.role === 'agent';
  const navItems = isAgent ? agentNavItems : adminNavItems;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-50 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300`}>
        <div className="p-4 border-b border-[#D4AF37]/20">
          <img src={LOGO_URL} alt="Logo" className="h-10" />
        </div>
        
        <nav className="py-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item flex items-center ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`admin-nav-${item.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#D4AF37]/20">
          <div className="flex items-center gap-2 mb-2">
            {admin.photo_url ? (
              <img src={admin.photo_url} alt="" className="w-7 h-7 rounded-full object-cover border border-[#D4AF37]/30" />
            ) : (
              <Shield className="w-3.5 h-3.5 text-[#D4AF37]" />
            )}
            <span className={`text-xs font-medium uppercase tracking-wider ${isAgent ? 'text-blue-400' : 'text-[#D4AF37]'}`}>
              {isAgent ? 'Agent' : 'Admin'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="truncate mr-2">
              <p className="text-[#F5F5F0] text-xs font-medium truncate">{admin.name}</p>
              <p className="text-[#808080] text-[10px] truncate">{admin.email}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="text-[#D4AF37] hover:text-[#F5F5F0] transition-colors flex-shrink-0"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden glass-header sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-[#F5F5F0]"
            data-testid="admin-mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img src={LOGO_URL} alt="Logo" className="h-8" />
          <div className="w-6" />
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
